import { useEffect, useState, useMemo } from "react";
import "./Routes.css";
import { Container, Modal, Form, Button, Spinner } from "react-bootstrap";
import Pagination from 'react-bootstrap/Pagination';
import { RootState } from "../../../store";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from 'react-router-dom';
import AdminDataTable, {
  TableColumn,
  TableAction,
  createStatusBadge,
  createDifficultyBadge,
  formatDate,
  formatElevationGain,
  truncateText
} from "../../Reusables/AdminDataTable/AdminDataTable";
import FilterBar, { FilterField } from "../../Reusables/FilterBar/FilterBar";
import { setRouteFilters } from "../../../store/slices/RouteFiltersSlice";
import { useRoutes } from "../../../components/Route/hooks/useRoutes";
import { showSuccess, showError } from "../../../Utilities/sweetAlert";
import { RouteStatus, RouteState } from "../../Data/Routes";

const Routes: React.FC = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const currentFilters = useSelector((state: RootState) => state.routeFilters);

    const {
        routes,
        totalRoutes,
        currentPage,
        totalPages,
        loading,
        activateRoute,
        deactivateRoute,
        setCurrentPage,
        fetchRoutes
    } = useRoutes({
        pageSize: 12,
        name: currentFilters.name,
        status: currentFilters.statusFilter,
        state: currentFilters.stateFilter,
        sortBy: currentFilters.sortBy,
    });

    const [showRejectModal, setShowRejectModal] = useState(false);
    const [selectedRoute, setSelectedRoute] = useState<any>(null);
    const [rejectionReason, setRejectionReason] = useState('');
    const [actionLoading, setActionLoading] = useState(false);

    const handleApprove = async (route: any) => {
        setActionLoading(true);
        try {
            const success = await activateRoute(route.id);
            if (success) {
                showSuccess('Route Approved', 'Route has been activated successfully!');
            } else {
                showError('Approval Failed', 'Failed to approve route. Please try again.');
            }
        } catch (error) {
            console.error('Error approving route:', error);
            showError('Approval Failed', 'Failed to approve route. Please try again.');
        } finally {
            setActionLoading(false);
        }
    };

    const openRejectModal = (route: any) => {
        setSelectedRoute(route);
        setRejectionReason('');
        setShowRejectModal(true);
    };

    const handleReject = async () => {
        if (!selectedRoute) return;

        setActionLoading(true);
        try {
            const success = await deactivateRoute(selectedRoute.id, rejectionReason || undefined);
            if (success) {
                setShowRejectModal(false);
                setSelectedRoute(null);
                setRejectionReason('');
                showSuccess('Route Rejected', 'Route has been deactivated successfully.');
            } else {
                showError('Rejection Failed', 'Failed to reject route. Please try again.');
            }
        } catch (error) {
            console.error('Error rejecting route:', error);
            showError('Rejection Failed', 'Failed to reject route. Please try again.');
        } finally {
            setActionLoading(false);
        }
    };

    const closeRejectModal = () => {
        setShowRejectModal(false);
        setSelectedRoute(null);
        setRejectionReason('');
    };

    const handlePageChange = (newPage: number) => {
        const validNewPage = Math.min(Math.max(newPage, 1), totalPages);
        setCurrentPage(validNewPage);
    };

    useEffect(() => {
        fetchRoutes({
            pageSize: 12,
            page: currentPage,
            name: currentFilters.name,
            status: currentFilters.statusFilter,
            state: currentFilters.stateFilter,
            sortBy: currentFilters.sortBy,
        });
    }, [currentFilters, currentPage]);

    const filterFields: FilterField[] = useMemo(() => [
        {
            name: 'name',
            label: 'Route Name',
            type: 'text',
            placeholder: 'Search by name...',
            defaultValue: currentFilters.name || ''
        },
        {
            name: 'statusFilter',
            label: 'Status',
            type: 'select',
            options: [
                { value: RouteStatus.Pending, label: 'Pending' },
                { value: RouteStatus.Active, label: 'Active' },
                { value: RouteStatus.Inactive, label: 'Inactive' }
            ],
            defaultValue: currentFilters.statusFilter || ''
        },
        {
            name: 'stateFilter',
            label: 'State',
            type: 'select',
            options: [
                { value: RouteState.Draft, label: 'Draft' },
                { value: RouteState.Personal, label: 'Personal' },
                { value: RouteState.Public, label: 'Public' }
            ],
            defaultValue: currentFilters.stateFilter || ''
        },
        {
            name: 'sortBy',
            label: 'Sort By',
            type: 'select',
            options: [
                { value: 'CreatedAtNewToOld', label: 'Newest First' },
                { value: 'CreatedAtOldToNew', label: 'Oldest First' }
            ],
            defaultValue: currentFilters.sortBy || ''
        }
    ], [currentFilters]);

    const tableColumns: TableColumn[] = [
        {
            key: 'name',
            label: 'Name',
            sortable: true,
            width: '20%',
            render: (value, row) => (
                <div>
                    <strong>{truncateText(value || 'Unnamed Route', 30)}</strong>
                    <br />
                    <small className="text-muted">ID: {row.id}</small>
                </div>
            )
        },
        {
            key: 'creator',
            label: 'Creator',
            width: '15%',
            render: (value) => (
                <div>
                    <span>{value?.name || 'Unknown'}</span>
                    <br />
                    <small className="text-muted">{value?.email || ''}</small>
                </div>
            )
        },
        {
            key: 'state',
            label: 'State',
            width: '10%',
            render: (value) => {
                const colors: Record<string, string> = {
                    draft: 'secondary',
                    personal: 'info',
                    public: 'primary',
                };
                return (
                    <span className={`badge bg-${colors[value] || 'secondary'}`}>
                        {value}
                    </span>
                );
            }
        },
        {
            key: 'status',
            label: 'Status',
            width: '10%',
            render: (value) => createStatusBadge(value)
        },
        {
            key: 'difficulty',
            label: 'Difficulty',
            width: '10%',
            render: (value) => value ? createDifficultyBadge(value) : <span className="text-muted">N/A</span>
        },
        {
            key: 'distance',
            label: 'Distance',
            width: '8%',
            render: (value) => value ? `${value} km` : 'N/A'
        },
        {
            key: 'elevationGain',
            label: 'Elevation',
            width: '10%',
            render: (value) => formatElevationGain(value)
        },
        {
            key: 'createdAt',
            label: 'Created',
            width: '10%',
            render: (value) => formatDate(value)
        },
        {
            key: 'photos',
            label: 'Photos',
            width: '6%',
            render: (value) => (
                <span className="badge bg-info">
                    {value ? value.filter((p: any) => p != null).length : 0}
                </span>
            )
        },
    ];

    const tableActions: TableAction[] = [
        {
            label: 'View/Edit',
            variant: 'primary',
            icon: 'fas fa-edit',
            onClick: (row) => navigate(`/route/edit/${row.id}`)
        },
        {
            label: 'Approve',
            variant: 'success',
            icon: 'fas fa-check',
            onClick: (row) => handleApprove(row),
            show: (row) => row.state !== 'draft' && row.status === 'pending'
        },
        {
            label: 'Reject',
            variant: 'danger',
            icon: 'fas fa-times',
            onClick: (row) => openRejectModal(row),
            show: (row) => row.state !== 'draft' && (row.status === 'pending' || row.status === 'active')
        }
    ];

    const handleFilterChange = (filters: Record<string, any>) => {
        dispatch(setRouteFilters({
            name: filters.name || '',
            statusFilter: filters.statusFilter || '',
            stateFilter: filters.stateFilter || '',
            sortBy: filters.sortBy || '',
        }));
        setCurrentPage(1);
    };

    return (
        <Container fluid className="admin-page-container py-4">
            <FilterBar
                title="User Route Filters"
                fields={filterFields}
                onFilter={handleFilterChange}
                showAddButton={false}
            />

            <AdminDataTable
                data={routes}
                columns={tableColumns}
                actions={tableActions}
                loading={loading}
                emptyMessage="No routes found. Try adjusting your filters."
            />

            {totalPages > 1 && (
                <div className="d-flex justify-content-center mt-4">
                    <Pagination>
                        <Pagination.First
                            onClick={() => handlePageChange(1)}
                            disabled={currentPage === 1}
                        />
                        <Pagination.Prev
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                        />

                        {[...Array(totalPages)].map((_, index) => (
                            <Pagination.Item
                                key={index + 1}
                                active={index + 1 === currentPage}
                                onClick={() => handlePageChange(index + 1)}
                            >
                                {index + 1}
                            </Pagination.Item>
                        ))}

                        <Pagination.Next
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                        />
                        <Pagination.Last
                            onClick={() => handlePageChange(totalPages)}
                            disabled={currentPage === totalPages}
                        />
                    </Pagination>
                </div>
            )}

            <div className="d-flex justify-content-center mt-3">
                <div className="total-count-display">
                    <span className="text-muted">
                        Showing {routes.length} of {totalRoutes} total routes
                    </span>
                </div>
            </div>

            {/* Rejection Reason Modal */}
            <Modal show={showRejectModal} onHide={closeRejectModal} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Reject Route</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>
                        Are you sure you want to reject <strong>{selectedRoute?.name || 'this route'}</strong>?
                    </p>
                    <Form.Group>
                        <Form.Label>Rejection Reason (optional)</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            placeholder="Enter a reason for rejection..."
                            value={rejectionReason}
                            onChange={(e) => setRejectionReason(e.target.value)}
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={closeRejectModal}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={handleReject} disabled={actionLoading}>
                        {actionLoading ? (
                            <Spinner animation="border" size="sm" />
                        ) : (
                            'Reject Route'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
}

export default Routes;
